import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, UploadIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { DatePicker } from '../../components/ui/DatePicker';
import { Modal } from '../../components/ui/Modal';
interface BankTransactionFormData {
  transactionType: 'Withdrawal' | 'Deposit' | 'Bank Transfer';
  voucherDate: string;
  bankNameId: string;
  bankAccountId: string;
  chartOfAccountId?: string;
  amount: number;
  checkNo?: string;
  fromBankNameId?: string;
  fromBankAccountId?: string;
  fromChartOfAccountId?: string;
  toBankNameId?: string;
  toBankAccountId?: string;
  toChartOfAccountId?: string;
  narration: string;
  purpose: string;
  notes: string;
  attachment?: File;
}
export function BankTransactionCreate() {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formData, setFormData] = useState<BankTransactionFormData>({
    transactionType: 'Withdrawal',
    voucherDate: new Date().toISOString().split('T')[0],
    bankNameId: '',
    bankAccountId: '',
    amount: 0,
    narration: '',
    purpose: '',
    notes: ''
  });
  const transactionTypes = [{
    value: 'Withdrawal',
    label: 'Withdrawal'
  }, {
    value: 'Deposit',
    label: 'Deposit'
  }, {
    value: 'Bank Transfer',
    label: 'Bank Transfer'
  }];
  const mockBankNames = [{
    value: 'bank1',
    label: 'Premier Bank'
  }, {
    value: 'bank2',
    label: 'Dahabshiil Bank'
  }, {
    value: 'bank3',
    label: 'Salaam Bank'
  }];
  const mockBankAccounts: Record<string, Array<{
    value: string;
    label: string;
    coaId: string;
    coaName: string;
  }>> = {
    bank1: [{
      value: 'acc1',
      label: 'Hargeisa Branch - 1234567890',
      coaId: 'coa1',
      coaName: '1200 - Premier Bank Account'
    }, {
      value: 'acc2',
      label: 'Mogadishu Branch - 1234567891',
      coaId: 'coa2',
      coaName: '1201 - Premier Bank - Mogadishu'
    }],
    bank2: [{
      value: 'acc3',
      label: 'Berbera Branch - 9876543210',
      coaId: 'coa3',
      coaName: '1210 - Dahabshiil Bank Account'
    }, {
      value: 'acc4',
      label: 'Hargeisa Branch - 9876543211',
      coaId: 'coa4',
      coaName: '1211 - Dahabshiil - Hargeisa'
    }],
    bank3: [{
      value: 'acc5',
      label: 'Main Branch - 5555555555',
      coaId: 'coa5',
      coaName: '1220 - Salaam Bank Account'
    }]
  };
  const getBankAccounts = (bankNameId: string) => {
    return mockBankAccounts[bankNameId] || [];
  };
  const getChartOfAccount = (bankNameId: string, bankAccountId: string) => {
    const accounts = mockBankAccounts[bankNameId] || [];
    const account = accounts.find(acc => acc.value === bankAccountId);
    return account ? account.coaName : '';
  };
  const handleBankNameChange = (bankNameId: string, isFrom = false, isTo = false) => {
    if (isFrom) {
      setFormData({
        ...formData,
        fromBankNameId: bankNameId,
        fromBankAccountId: '',
        fromChartOfAccountId: ''
      });
    } else if (isTo) {
      setFormData({
        ...formData,
        toBankNameId: bankNameId,
        toBankAccountId: '',
        toChartOfAccountId: ''
      });
    } else {
      setFormData({
        ...formData,
        bankNameId,
        bankAccountId: '',
        chartOfAccountId: ''
      });
    }
  };
  const handleBankAccountChange = (bankAccountId: string, isFrom = false, isTo = false) => {
    if (isFrom) {
      const coaName = getChartOfAccount(formData.fromBankNameId!, bankAccountId);
      setFormData({
        ...formData,
        fromBankAccountId: bankAccountId,
        fromChartOfAccountId: coaName
      });
    } else if (isTo) {
      const coaName = getChartOfAccount(formData.toBankNameId!, bankAccountId);
      setFormData({
        ...formData,
        toBankAccountId: bankAccountId,
        toChartOfAccountId: coaName
      });
    } else {
      const coaName = getChartOfAccount(formData.bankNameId, bankAccountId);
      setFormData({
        ...formData,
        bankAccountId,
        chartOfAccountId: coaName
      });
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Only PDF, JPG, and PNG files are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setFormData({
        ...formData,
        attachment: file
      });
    }
  };
  const validateForm = (): boolean => {
    const today = new Date().toISOString().split('T')[0];
    if (formData.voucherDate > today) {
      alert('Voucher date cannot be in the future');
      return false;
    }
    if (formData.transactionType === 'Bank Transfer') {
      if (!formData.fromBankNameId || !formData.fromBankAccountId) {
        alert('Please select From Bank Name and Account');
        return false;
      }
      if (!formData.toBankNameId || !formData.toBankAccountId) {
        alert('Please select To Bank Name and Account');
        return false;
      }
    } else {
      if (!formData.bankNameId || !formData.bankAccountId) {
        alert('Please select Bank Name and Account');
        return false;
      }
    }
    if (formData.amount <= 0) {
      alert('Amount must be greater than zero');
      return false;
    }
    if (!formData.narration.trim()) {
      alert('Narration is required');
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmModal(true);
    }
  };
  const handleConfirm = async () => {
    try {
      console.log('Creating bank transaction:', formData);
      // Accounting logic:
      // Withdrawal: Cash Dr., Bank Cr.
      // Deposit: Bank Dr., Cash Cr.
      // Bank Transfer: Source Bank Cr., Destination Bank Dr.
      navigate('/bank-transaction');
    } catch (error: any) {
      alert(error.message);
    } finally {
      setShowConfirmModal(false);
    }
  };
  const isBankTransfer = formData.transactionType === 'Bank Transfer';
  const isWithdrawal = formData.transactionType === 'Withdrawal';
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-398">
      <div className="max-w-4xl mx-auto space-y-6" data-id="element-399">
        {/* Header */}
        <div className="flex items-center gap-4" data-id="element-400">
          <Button variant="ghost" onClick={() => navigate('/bank-transaction')} data-id="element-401">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-402" />
          </Button>
          <div data-id="element-403">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-404">
              Create Bank Transaction
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-405">
              Record bank withdrawal, deposit, or transfer
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-6 space-y-6" data-id="element-406">
          {/* Transaction Type & Date */}
          <div data-id="element-407">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-408">
              Transaction Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-409">
              <Select label="Transaction Type" required options={transactionTypes} value={formData.transactionType} onChange={e => setFormData({
              ...formData,
              transactionType: e.target.value as 'Withdrawal' | 'Deposit' | 'Bank Transfer',
              bankNameId: '',
              bankAccountId: '',
              chartOfAccountId: '',
              checkNo: '',
              fromBankNameId: '',
              fromBankAccountId: '',
              fromChartOfAccountId: '',
              toBankNameId: '',
              toBankAccountId: '',
              toChartOfAccountId: ''
            })} data-id="element-410" />

              <DatePicker label="Voucher Date" required value={formData.voucherDate} onChange={date => setFormData({
              ...formData,
              voucherDate: date
            })} max={new Date().toISOString().split('T')[0]} helperText="Cannot be future date" data-id="element-411" />
            </div>
          </div>

          {/* Accounting Info */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-412">
            <h4 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-413">
              Accounting Entry
            </h4>
            <p className="text-sm text-blue-800" data-id="element-414">
              {formData.transactionType === 'Withdrawal' && '• Cash Dr. | Bank Cr.'}
              {formData.transactionType === 'Deposit' && '• Bank Dr. | Cash Cr.'}
              {formData.transactionType === 'Bank Transfer' && '• Source Bank Cr. | Destination Bank Dr.'}
            </p>
          </div>

          {/* Bank Selection - For Withdrawal/Deposit */}
          {!isBankTransfer && <div data-id="element-415">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-416">
                Bank Selection
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-417">
                <Select label="Bank Name" required options={mockBankNames} value={formData.bankNameId} onChange={e => handleBankNameChange(e.target.value)} data-id="element-418" />

                <Select label="Bank Account" required options={getBankAccounts(formData.bankNameId)} value={formData.bankAccountId} onChange={e => handleBankAccountChange(e.target.value)} disabled={!formData.bankNameId} helperText="Branch Name - Account Number" data-id="element-419" />

                {formData.chartOfAccountId && <div className="md:col-span-2" data-id="element-420">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-421">
                      Associated Chart of Account
                    </label>
                    <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900" data-id="element-422">
                      {formData.chartOfAccountId}
                    </div>
                  </div>}
              </div>
            </div>}

          {/* Bank Transfer - From/To Banks */}
          {isBankTransfer && <>
              <div data-id="element-423">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-424">
                  From Bank (Source)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-425">
                  <Select label="From Bank Name" required options={mockBankNames} value={formData.fromBankNameId} onChange={e => handleBankNameChange(e.target.value, true, false)} data-id="element-426" />

                  <Select label="From Bank Account" required options={getBankAccounts(formData.fromBankNameId || '')} value={formData.fromBankAccountId} onChange={e => handleBankAccountChange(e.target.value, true, false)} disabled={!formData.fromBankNameId} helperText="Branch Name - Account Number" data-id="element-427" />

                  {formData.fromChartOfAccountId && <div className="md:col-span-2" data-id="element-428">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-429">
                        Associated Chart of Account
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900" data-id="element-430">
                        {formData.fromChartOfAccountId}
                      </div>
                    </div>}
                </div>
              </div>

              <div data-id="element-431">
                <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-432">
                  To Bank (Destination)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-433">
                  <Select label="To Bank Name" required options={mockBankNames} value={formData.toBankNameId} onChange={e => handleBankNameChange(e.target.value, false, true)} data-id="element-434" />

                  <Select label="To Bank Account" required options={getBankAccounts(formData.toBankNameId || '')} value={formData.toBankAccountId} onChange={e => handleBankAccountChange(e.target.value, false, true)} disabled={!formData.toBankNameId} helperText="Branch Name - Account Number" data-id="element-435" />

                  {formData.toChartOfAccountId && <div className="md:col-span-2" data-id="element-436">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-437">
                        Associated Chart of Account
                      </label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-900" data-id="element-438">
                        {formData.toChartOfAccountId}
                      </div>
                    </div>}
                </div>
              </div>
            </>}

          {/* Amount & Check No */}
          <div data-id="element-439">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-440">
              Transaction Amount
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-441">
              <Input label="Amount" type="number" required value={formData.amount} onChange={e => setFormData({
              ...formData,
              amount: Number(e.target.value)
            })} min="0" step="0.01" helperText="Must be greater than zero" data-id="element-442" />

              {isWithdrawal && <Input label="Check No" value={formData.checkNo} onChange={e => setFormData({
              ...formData,
              checkNo: e.target.value
            })} placeholder="Optional check number" helperText="Optional for withdrawals" data-id="element-443" />}

              <Input label="Purpose" value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} placeholder="e.g., Office expenses" data-id="element-444" />
            </div>
          </div>

          {/* Narration & Notes */}
          <div data-id="element-445">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-446">
              Additional Information
            </h3>
            <div className="space-y-4" data-id="element-447">
              <div data-id="element-448">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-449">
                  Narration <span className="text-red-500" data-id="element-450">*</span>
                </label>
                <textarea required className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.narration} onChange={e => setFormData({
                ...formData,
                narration: e.target.value
              })} placeholder="Enter transaction narration (required)..." data-id="element-451" />
              </div>

              <div data-id="element-452">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-453">
                  Notes
                </label>
                <textarea className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" rows={3} value={formData.notes} onChange={e => setFormData({
                ...formData,
                notes: e.target.value
              })} placeholder="Additional notes or remarks..." data-id="element-454" />
              </div>

              <div data-id="element-455">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-456">
                  Attachment <span className="text-gray-500" data-id="element-457">(Optional)</span>
                </label>
                <div className="flex items-center gap-2" data-id="element-458">
                  <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50" data-id="element-459">
                    <UploadIcon className="w-4 h-4 text-gray-600" data-id="element-460" />
                    <span className="text-sm text-gray-700" data-id="element-461">Choose File</span>
                    <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFileChange} data-id="element-462" />
                  </label>
                  {formData.attachment && <span className="text-sm text-gray-600" data-id="element-463">
                      {formData.attachment.name}
                    </span>}
                </div>
                <p className="text-xs text-gray-500 mt-1" data-id="element-464">
                  PDF, JPG, PNG (Max 5MB)
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t" data-id="element-465">
            <Button type="button" variant="secondary" onClick={() => navigate('/bank-transaction')} data-id="element-466">
              Cancel
            </Button>
            <Button type="submit" variant="primary" data-id="element-467">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-468" />
              Create Bank Transaction
            </Button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Bank Transaction" footer={<>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)} data-id="element-470">
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirm} data-id="element-471">
              Confirm
            </Button>
          </>} data-id="element-469">
        <div className="space-y-3" data-id="element-472">
          <p className="text-gray-700" data-id="element-473">
            Please review the bank transaction details:
          </p>
          <div className="p-3 bg-gray-50 rounded text-sm space-y-1" data-id="element-474">
            <div data-id="element-475">
              <strong data-id="element-476">Type:</strong> {formData.transactionType}
            </div>
            <div data-id="element-477">
              <strong data-id="element-478">Date:</strong> {formData.voucherDate}
            </div>
            <div data-id="element-479">
              <strong data-id="element-480">Amount:</strong> ${formData.amount.toLocaleString()}
            </div>
            <div data-id="element-481">
              <strong data-id="element-482">Narration:</strong> {formData.narration}
            </div>
          </div>
          <p className="text-sm text-gray-600" data-id="element-483">
            This will create a voucher and update the ledgers automatically.
          </p>
        </div>
      </Modal>
    </div>;
}